import React, { useMemo, useRef, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { AD_VIEWABILITY_CONFIG, useAdsContext } from '@/contexts/ads-context';
import { AdImpressionSource } from '@/contexts/ads-context-types';
import {
  AllowedShortcutNumbers,
  AllowedShortcuts,
  numberKeyMap,
} from '@/platform/shortcuts/operator-shortcuts';
import {
  AvailabilityMap,
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import {
  CARD_VIEW_V2_GRID_CELL_LIST_ITEM_STYLE,
  CARD_VIEW_V2_GRID_LIST_ITEM_STYLE,
} from '@/utils/card-view-v2-layout';
import { createStyles } from '@/contexts/theme-context';
import { findFirstPaidSearchSku } from '@/utils/ads';
import { LIST_HEIGHT as HORIZONTAL_SELECTOR_HEIGHT } from '@/components/horizontal-selector';
import { Key } from '@platform/shortcuts/utils/keyboard-platform';
import {
  Keyboard,
  ScrollViewProps,
  StyleProp,
  useWindowDimensions,
  ViewStyle,
} from 'react-native';
import { LIST_ITEM_SEPARATOR_HEIGHT, SCREEN_PADDING } from '@/styles';
import {
  Props as ListItemProps,
  ListOptionValue,
} from '@/components/list-item';
import { MinimalOrderableItemListItemV2 } from '@/components/minimal-orderable-item-list-item-v2';
import { OrderableItemList } from './orderable-item-list';
import { OrderableItemListItemWithRowQuantity } from './orderable-item-list-item';
import { PaidSearchItemHeader } from '@/components/ads/paid-search-item-header';
import { RecyclerListRef } from '@/components/recycler-list';
import { useCardViewV2ProductGridLayout } from '@/hooks/use-card-view-v2-product-grid-layout';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useKeyboardShortcuts } from '@platform/shortcuts/hooks/use-keyboard-shortcuts';
import { useMultiOrderGuideButton } from '@/hooks/use-multi-order-guide-button';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useShowListView } from '@/hooks/use-show-list-view';
import { useSupplierDynamicConfigValue } from '@/hooks/use-dynamic-config-value';

export type RenderActionsWithSourceFn = (
  sku: ItemListOptionMetadata,
  source: 'list' | 'sheet',
) => React.ReactNode;

interface Props {
  availabilityMap?: AvailabilityMap;
  disableTextInput?: boolean;
  enforceSingleColumn?: boolean;
  highlightedItemValues?: ListOptionValue[];
  isInboundOrder?: boolean;
  items: ItemListOption[];
  listContentContainerStyle?: StyleProp<ViewStyle>;
  listItemProps?: Partial<ListItemProps<ItemListOption>>;
  listItemSeparatorStyle?: StyleProp<ViewStyle>;
  loadingMore?: boolean;
  onEndReached?: () => void;
  renderActions?: RenderActionsWithSourceFn;
  renderNote?: (skuMetadata: ItemListOptionMetadata) => React.ReactNode;
  renderParLevels?: boolean;
  renderSource: string;
  scrollViewProps?: ScrollViewProps;
  searchText?: string;
  showSingleOption?: boolean;
  showVariantPackOrderInfo?: boolean;
  skipListInteractionCheck?: boolean;
}

export const SupplierCatalogList: React.FC<Props> = ({
  availabilityMap,
  disableTextInput,
  enforceSingleColumn,
  highlightedItemValues,
  items,
  listContentContainerStyle,
  listItemProps,
  listItemSeparatorStyle,
  loadingMore,
  renderActions,
  renderNote,
  renderParLevels,
  renderSource,
  scrollViewProps,
  searchText,
  onEndReached,
  showSingleOption = true,
  showVariantPackOrderInfo = false,
  isInboundOrder = false,
  skipListInteractionCheck = false,
}) => {
  const [styles] = useStyles();
  const { bottom } = useSafeAreaInsets();
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const { selectItemListOption } = useItemDetailContext();
  const { height } = useWindowDimensions();
  const { onViewableAdItemsChanged } = useAdsContext();
  const { showListView, showListViewToggle } = useShowListView({
    enableOnSmallLayouts: true,
  });
  const listUsesListDisplayMode = !enforceSingleColumn && showListView;
  const listRowItemLayout = listUsesListDisplayMode && showListViewToggle;

  const cardViewV2Enabled = useSupplierDynamicConfigValue(
    'card_view_v2_enabled',
    false,
  );
  const shouldUseCardV2Grid =
    cardViewV2Enabled &&
    !listUsesListDisplayMode &&
    !enforceSingleColumn &&
    !isInboundOrder;

  const cardV2Grid = useCardViewV2ProductGridLayout(shouldUseCardV2Grid);

  const {
    renderOrderGuideButton,
    shouldShowOrderGuideButton,
    renderPartitionSelectorSheet,
  } = useMultiOrderGuideButton();

  const listRef = useRef<RecyclerListRef<ItemListOption> | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const centerItemAtIndex = (index: number) => {
    if (listRef.current && items.length > 0) {
      const itemHeight = HORIZONTAL_SELECTOR_HEIGHT;
      const halfScreenHeight = height / 2;
      const offset = index * itemHeight - halfScreenHeight + itemHeight / 2;
      const viewOffset = Math.max(0, offset);

      listRef.current.scrollToIndex({ index, viewOffset });
    }
  };

  const focusItemAtIndex = (index: number) => {
    if (index >= 0 && index < items.length) {
      setFocusedIndex(index);
      centerItemAtIndex(index);
    }
  };

  const moveFocusUp = () => {
    if (focusedIndex === null) {
      focusItemAtIndex(items.length - 1);
    } else {
      focusItemAtIndex((focusedIndex - 1 + items.length) % items.length);
    }
  };

  const moveFocusDown = () => {
    if (focusedIndex === null) {
      focusItemAtIndex(0);
    } else {
      focusItemAtIndex((focusedIndex + 1) % items.length);
    }
  };

  useKeyboardShortcuts(
    AllowedShortcutNumbers,
    (_, handler) => {
      const key = handler.keys?.join().toLowerCase();
      const numberPressed = numberKeyMap[`${Key.Alt}+${key}`];
      if (numberPressed !== undefined) {
        const index = numberPressed - 1;
        focusItemAtIndex(index);
      }
    },
    [items],
  );

  useKeyboardShortcuts(AllowedShortcuts.ArrowUp, moveFocusUp, [
    focusedIndex,
    items,
  ]);

  useKeyboardShortcuts(AllowedShortcuts.ArrowDown, moveFocusDown, [
    focusedIndex,
    items,
  ]);

  const firstPaidSearchItem = useMemo(
    () => items.find((item) => findFirstPaidSearchSku(item)),
    [items],
  );

  const computedListItemProps: Partial<ListItemProps<ItemListOption>> =
    useMemo(() => {
      return {
        ...(shouldUseCardV2Grid
          ? { listItemStyle: CARD_VIEW_V2_GRID_CELL_LIST_ITEM_STYLE }
          : {}),
        onSelect: (i) => {
          Keyboard.dismiss();
          selectItemListOption(
            i,
            {
              searchText,
              source: renderSource,
            },
            {
              availableQuantityMap: availabilityMap,
              renderActions: renderActions
                ? (sku) => renderActions(sku, 'sheet')
                : undefined,
            },
          );
        },
        renderListItemHeader: (option, columnCount) => {
          return Boolean(findFirstPaidSearchSku(option)) &&
            // Show paid search header for all items in multi column grid layouts
            // Otherwise only show for first paid search item
            ((typeof columnCount === 'number' && columnCount > 1) ||
              option === firstPaidSearchItem) ? (
            <PaidSearchItemHeader showListView={listUsesListDisplayMode} />
          ) : null;
        },
        renderMainContent: (option) => {
          if (shouldUseCardV2Grid) {
            return (
              <MinimalOrderableItemListItemV2
                item={option}
                onSelect={() => {
                  Keyboard.dismiss();
                  selectItemListOption(
                    option,
                    {
                      searchText,
                      source: renderSource,
                    },
                    {
                      availableQuantityMap: availabilityMap,
                      renderActions: renderActions
                        ? (sku) => renderActions(sku, 'sheet')
                        : undefined,
                    },
                  );
                }}
                size="large"
                style={styles.cardV2ListItem}
                updateQuantity={(updateQuantityData) => {
                  debouncedUpdateQuantity(updateQuantityData, {
                    source: renderSource,
                  });
                }}
              />
            );
          }

          const showOrderGuideButton = shouldShowOrderGuideButton(option);

          return (
            <OrderableItemListItemWithRowQuantity
              availabilityMap={availabilityMap}
              containerStyle={
                option === firstPaidSearchItem
                  ? styles.paidSearchListViewMargin
                  : null
              }
              disableTextInput={disableTextInput}
              isCompactView={isInboundOrder}
              isFocused={items.indexOf(option) === focusedIndex}
              isInboundOrder={isInboundOrder}
              onOptionsPress={(i) => {
                selectItemListOption(i, { source: renderSource });
              }}
              option={option}
              renderActions={
                renderActions ? (sku) => renderActions(sku, 'list') : undefined
              }
              renderNote={renderNote}
              renderOrderGuideButton={
                showOrderGuideButton ? renderOrderGuideButton : undefined
              }
              renderParLevels={renderParLevels}
              showListView={listRowItemLayout}
              showSingleOption={showSingleOption}
              showVariantPackOrderInfo={showVariantPackOrderInfo}
              source={renderSource}
              updateQuantity={(updateQuantityData) => {
                debouncedUpdateQuantity(updateQuantityData, {
                  source: renderSource,
                });
              }}
            />
          );
        },
        ...listItemProps,
      };
    }, [
      listItemProps,
      selectItemListOption,
      searchText,
      renderSource,
      availabilityMap,
      renderActions,
      firstPaidSearchItem,
      listUsesListDisplayMode,
      showSingleOption,
      disableTextInput,
      items,
      focusedIndex,
      renderNote,
      renderOrderGuideButton,
      renderParLevels,
      shouldShowOrderGuideButton,
      debouncedUpdateQuantity,
      showVariantPackOrderInfo,
      isInboundOrder,
      styles.paidSearchListViewMargin,
      listRowItemLayout,
      shouldUseCardV2Grid,
      styles.cardV2ListItem,
    ]);

  return (
    <>
      <OrderableItemList
        gridColumnCount={
          shouldUseCardV2Grid
            ? cardV2Grid.columnCount
            : enforceSingleColumn
              ? 1
              : undefined
        }
        hasExternalSeparatorControl={shouldUseCardV2Grid || isInboundOrder}
        highlightedItemValues={highlightedItemValues}
        keyboardFocusedIndex={focusedIndex}
        listContentContainerStyle={[
          listContentContainerStyle,
          shouldUseCardV2Grid ? cardV2Grid.listContentContainerStyle : null,
          { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
        ]}
        listData={items || []}
        ListFooterComponent={loadingMore ? <ActivityIndicator /> : null}
        listItemProps={computedListItemProps}
        listItemSeparatorStyle={[
          listItemSeparatorStyle,
          isInboundOrder ? styles.listItemSeparator : null,
        ]}
        listRef={listRef}
        listRowItemLayout={listRowItemLayout}
        onEndReached={onEndReached}
        onViewableItemsChanged={({ viewableItems }) =>
          onViewableAdItemsChanged(viewableItems, AdImpressionSource.Search)
        }
        scrollViewProps={scrollViewProps}
        shouldRenderSeparator={
          shouldUseCardV2Grid
            ? () => false
            : (leadingItem) => !findFirstPaidSearchSku(leadingItem)
        }
        showListView={listUsesListDisplayMode}
        skipInteractionCheck={skipListInteractionCheck}
        viewabilityConfig={AD_VIEWABILITY_CONFIG}
      />
      {renderPartitionSelectorSheet()}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  cardV2ListItem: {
    ...CARD_VIEW_V2_GRID_LIST_ITEM_STYLE,
  },
  listItemSeparator: {
    backgroundColor: theme.sk.color.border.primary,
    height: LIST_ITEM_SEPARATOR_HEIGHT,
    marginHorizontal: 0,
  },
  paidSearchListViewMargin: {
    marginTop: 0,
  },
}));
